import { Component, inject, signal, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { IUser, IRole, UserApiService, AppService } from '@mavles/user';
import { AlertService } from '@mavles/shared';
import { StaticDataService } from '@mavles/shared';
import { Router } from '@angular/router';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
  standalone: false,
})
export class UserManagementComponent implements OnInit {
  private userApi: UserApiService = inject(UserApiService);
  private alertService = inject(AlertService);
  private modal = inject(NgbModal);
  private fb = inject(FormBuilder);
  public staticDataService = inject(StaticDataService);
  private appService = inject(AppService);
  private router = inject(Router);

  // ── State ──────────────────────────────────────────────────────────────────
  users = signal<IUser[]>([]);
  roles = signal<IRole[]>([]);
  loading = signal(false);
  saving = signal(false);

  selectedUser: IUser | null = null;

  userForm!: FormGroup;
  resetPasswordForm!: FormGroup;
  isEditMode = false;
  imagePreview: string | null = null;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    if (!this.appService.isAdmin()) {
      this.alertService.error('Access Denied: Administrative privileges required.');
      this.router.navigate(['/home']);
      return;
    }
    this.loadUsers();
    this.loadRoles();
    this.buildUserForm();
    this.buildResetForm();
  }

  // ── Data ───────────────────────────────────────────────────────────────────

  loadUsers(): void {
    this.loading.set(true);
    this.userApi.listAllUsers().subscribe({
      next: (res) => {
        this.users.set(res.data ?? (res as any));
        this.loading.set(false);
      },
      error: () => {
        this.alertService.error('Failed to load users');
        this.loading.set(false);
      },
    });
  }

  loadRoles(): void {
    this.roles.set(this.staticDataService.appConfig.roles);
  }

  // ── Toggle Status ───────────────────────────────────────────────────────────

  onToggleStatus(user: IUser, value: boolean): void {
    this.userApi.toggleStatus(+user.id, value).subscribe({
      next: () => {
        this.users.update((list) =>
          list.map((u) => (u.id === user.id ? { ...u, is_active: value } : u)),
        );
        this.alertService.success(
          `User ${value ? 'activated' : 'deactivated'}`,
        );
      },
      error: () => this.alertService.error('Failed to update status'),
    });
  }

  // ── User Form ───────────────────────────────────────────────────────────────

  buildUserForm(user?: IUser): void {
    this.userForm = this.fb.group({
      firstname: [user?.firstname ?? '', Validators.required],
      lastname: [user?.lastname ?? '', Validators.required],
      email: [user?.email ?? '', [Validators.required, Validators.email]],
      password: [
        '',
        this.isEditMode ? [] : [Validators.required, Validators.minLength(6)],
      ],
      role_ids: [
        user?.roles?.map((r: IRole) => r.id) ?? [],
        Validators.required,
      ],
      image: [user?.image ?? null],
    });
  }

  openCreateModal(content: any): void {
    this.isEditMode = false;
    this.selectedUser = null;
    this.imagePreview = null;
    this.buildUserForm();
    this.modal.open(content, { size: 'lg', centered: true });
  }

  openEditModal(user: IUser, content: any): void {
    this.isEditMode = true;
    this.selectedUser = user;
    this.imagePreview = null;
    this.buildUserForm(user);
    this.modal.open(content, { size: 'lg', centered: true });
  }

  onImageChange(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreview = e.target?.result as string;
      this.userForm.patchValue({ image: this.imagePreview });
    };
    reader.readAsDataURL(file);
  }

  onSaveUser(modal: any): void {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    const v = this.userForm.value;

    if (this.isEditMode && this.selectedUser) {
      const payload = { ...v };
      if (!payload.password) delete payload.password;

      this.userApi.updateUserAdmin(+this.selectedUser.id, payload).subscribe({
        next: (res) => {
          this.users.update((list) =>
            list.map((u) => (u.id === this.selectedUser!.id ? res.data : u)),
          );
          this.saving.set(false);
          modal.close();
          this.alertService.success('User updated successfully');
        },
        error: () => {
          this.saving.set(false);
          this.alertService.error('Failed to update user');
        },
      });
    } else {
      this.userApi.createUser(v).subscribe({
        next: (res) => {
          this.users.update((list) => [...list, res.data]);
          this.saving.set(false);
          modal.close();
          this.alertService.success('User created successfully');
        },
        error: () => {
          this.saving.set(false);
          this.alertService.error('Failed to create user');
        },
      });
    }
  }

  // ── Reset Password ──────────────────────────────────────────────────────────

  buildResetForm(): void {
    this.resetPasswordForm = this.fb.group(
      {
        new_password: ['', [Validators.required, Validators.minLength(6)]],
        new_password_confirmation: ['', Validators.required],
      },
      { validators: this.passwordMatchValidator },
    );
  }

  openResetModal(user: IUser, content: any): void {
    this.selectedUser = user;
    this.buildResetForm();
    this.modal.open(content, { size: 'sm', centered: true });
  }

  onResetPassword(modal: any): void {
    if (this.resetPasswordForm.invalid) {
      this.resetPasswordForm.markAllAsTouched();
      return;
    }
    if (!this.selectedUser) return;
    this.saving.set(true);

    this.userApi
      .adminResetPassword(+this.selectedUser.id, this.resetPasswordForm.value)
      .subscribe({
        next: () => {
          this.saving.set(false);
          modal.close();
          this.alertService.success('Password reset successfully');
        },
        error: () => {
          this.saving.set(false);
          this.alertService.error('Failed to reset password');
        },
      });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private passwordMatchValidator(form: FormGroup) {
    const p = form.get('new_password')?.value;
    const c = form.get('new_password_confirmation')?.value;
    return p && c && p !== c ? { mismatch: true } : null;
  }

  isInvalid(field: string, form: FormGroup): boolean {
    const ctrl = form.get(field);
    return !!(ctrl?.invalid && (ctrl.touched || ctrl.dirty));
  }

  roleColor(name: string): string {
    const map: Record<string, string> = {
      ADMIN: 'danger',
      MANAGER: 'primary',
      STAFF: 'secondary',
      USER: 'info',
    };
    return map[name?.toUpperCase()] ?? 'secondary';
  }

  fullName(u: IUser): string {
    return `${u.firstname ?? ''} ${u.lastname ?? ''}`.trim();
  }
}



